import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Suggerimento (tooltip) che compare subito, appena il mouse passa sopra o ci si arriva con la tastiera.
 * Sta sopra a tutto (fuori dalla pagina), si ribalta sotto se non c'è spazio e non esce mai dallo schermo.
 * Sul telefono si apre toccando. Non blocca i clic: il mouse ci passa attraverso.
 */

export interface TipContent {
  title: string;
  /** Una frase per riga: brevi, una sola idea ciascuna */
  lines: string[];
  /** Elenco: uno sopra l'altro, con il pallino */
  items?: string[];
  /** In fondo, in piccolo: cosa puoi fare adesso */
  hint?: string;
}

/** Titolo, linea, poi il resto: se è lungo (oltre due righe) ogni idea va in una citazione con la linea a sinistra. */
function TipBody({ title, lines, items, hint }: TipContent) {
  const long = lines.join(' ').length > 90;
  return (
    <>
      <b className="tip-title">{title}</b>
      {(lines.length > 0 || (items?.length ?? 0) > 0) && <hr className="tip-sep" />}
      {lines.map((l, i) => (long ? <blockquote key={i} className="tip-q">{l}</blockquote> : <p key={i}>{l}</p>))}
      {items && items.length > 0 && (
        <ul className="tip-list">
          {items.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      )}
      {hint && <p className="tip-hint">{hint}</p>}
    </>
  );
}

/** Un testo semplice (di un «title») diventa titolo + frasi + consiglio finale. */
export function structure(text: string): TipContent {
  if (text.includes('\n')) {
    // Testo già a righe: la prima è il titolo, «• » sono elenco, «→ » è il consiglio finale
    const rows = text.split('\n').map((x) => x.trim()).filter(Boolean);
    const title = rows.shift() ?? '';
    return {
      title,
      lines: rows.filter((x) => !/^[•→]/.test(x)),
      items: rows.filter((x) => x.startsWith('•')).map((x) => x.replace(/^•\s*/, '')),
      hint: rows.find((x) => x.startsWith('→'))?.replace(/^→\s*/, ''),
    };
  }
  const flat = text.replace(/\s+/g, ' ').trim();
  const parts = flat.match(/[^.!?]+[.!?]*(?=\s|$)/g)?.map((x) => x.trim()).filter(Boolean) ?? [flat];
  let hint: string | undefined;
  if (parts.length > 1 && /^(Clicca|Fai clic|Tocca|Premi)/i.test(parts[parts.length - 1])) hint = parts.pop()!.replace(/\.$/, '');
  let title: string;
  const colon = parts[0]?.indexOf(':') ?? -1;
  if (colon > 0 && colon <= 40) {
    title = parts[0].slice(0, colon);
    parts[0] = parts[0].slice(colon + 1).trim();
  } else if (parts.length > 1 || hint) {
    title = parts.shift()!.replace(/\.$/, '');
  } else {
    title = parts.shift()!.replace(/\.$/, '');
  }
  // Un elenco scritto in fila (con «·» o con tante virgole) va messo uno sopra l'altro
  const lines: string[] = [];
  let items: string[] | undefined;
  for (const part of parts.filter(Boolean)) {
    const dots = part.split(' · ');
    const commas = part.replace(/\.$/, '').split(', ');
    if (!items && dots.length >= 2) items = dots.map((x) => x.replace(/\.$/, ''));
    else if (!items && commas.length >= 3) items = commas;
    else lines.push(part);
  }
  return { title, lines, items, hint };
}

const GAP = 10;
const MARGIN = 10;

export function Tip({ content, className, children }: { content: TipContent; className?: string; children: React.ReactNode }) {
  const host = useRef<HTMLSpanElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const touch = useRef(false);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; below: boolean; arrow: number } | null>(null);
  const id = useRef(`tip-${Math.random().toString(36).slice(2, 8)}`).current;

  const place = useCallback(() => {
    const h = host.current;
    const b = box.current;
    if (!h || !b) return;
    const r = h.getBoundingClientRect();
    const w = b.offsetWidth;
    const bh = b.offsetHeight;
    const vw = window.innerWidth;
    const below = r.top - bh - GAP < MARGIN && r.bottom + bh + GAP < window.innerHeight;
    const cx = r.left + r.width / 2;
    const left = Math.min(Math.max(cx - w / 2, MARGIN), Math.max(MARGIN, vw - w - MARGIN));
    const top = below ? r.bottom + GAP : r.top - bh - GAP;
    setPos({ left, top, below, arrow: Math.min(Math.max(cx - left, 16), w - 16) });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
    else setPos(null);
  }, [open, place]);

  useLayoutEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const key = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', key);
    };
  }, [open]);

  return (
    <span
      ref={host}
      className={`tip-host${className ? ` ${className}` : ''}`}
      tabIndex={0}
      aria-describedby={open ? id : undefined}
      onPointerDown={(e) => {
        touch.current = e.pointerType !== 'mouse';
      }}
      onPointerEnter={(e) => e.pointerType === 'mouse' && setOpen(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onClick={(e) => {
        // Il pallino o la scheda sotto non devono aprirsi quando si legge il suggerimento
        e.stopPropagation();
        if (touch.current) setOpen((o) => !o);
      }}
    >
      {children}
      {open &&
        createPortal(
          <div
            ref={box}
            id={id}
            role="tooltip"
            className={`tip-box${pos?.below ? ' below' : ''}`}
            style={{ left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden', '--arrow': `${pos?.arrow ?? 24}px` } as React.CSSProperties}
          >
            <TipBody {...content} />
          </div>,
          document.body,
        )}
    </span>
  );
}

/**
 * Tutti i «title» del programma (le scritte che compaiono passando il mouse) diventano suggerimenti subito visibili,
 * con lo stesso aspetto dei suggerimenti belli. Si montano una volta sola, in alto.
 */
export function GlobalTitleTips() {
  const box = useRef<HTMLDivElement>(null);
  const target = useRef<HTMLElement | null>(null);
  const [tip, setTip] = useState<{ text: string; left: number; top: number; below: boolean; arrow: number; shown: boolean } | null>(null);

  useLayoutEffect(() => {
    if (!tip || tip.shown || !box.current || !target.current) return;
    const r = target.current.getBoundingClientRect();
    const w = box.current.offsetWidth;
    const bh = box.current.offsetHeight;
    const below = r.top - bh - GAP < MARGIN && r.bottom + bh + GAP < window.innerHeight;
    const cx = r.left + r.width / 2;
    const left = Math.min(Math.max(cx - w / 2, MARGIN), Math.max(MARGIN, window.innerWidth - w - MARGIN));
    setTip({ ...tip, left, top: below ? r.bottom + GAP : r.top - bh - GAP, below, arrow: Math.min(Math.max(cx - left, 16), w - 16), shown: true });
  }, [tip]);

  useLayoutEffect(() => {
    const over = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const el = (e.target as HTMLElement | null)?.closest?.('[title],[data-tip]') as HTMLElement | null;
      if (!el) return;
      const text = (el.getAttribute('title') || el.getAttribute('data-tip'))?.trim();
      if (!text) return;
      if (!el.getAttribute('aria-label') && !el.textContent?.trim()) el.setAttribute('aria-label', text);
      el.setAttribute('data-tip', text); // il testo resta qui: vale anche dalla seconda volta
      el.removeAttribute('title'); // niente suggerimento lento del sistema
      target.current = el;
      setTip({ text, left: 0, top: 0, below: false, arrow: 24, shown: false });
    };
    const out = (e: PointerEvent) => {
      const el = target.current;
      if (el && !el.contains(e.relatedTarget as Node | null)) {
        target.current = null;
        setTip(null);
      }
    };
    const hide = () => {
      target.current = null;
      setTip(null);
    };
    document.addEventListener('pointerover', over, true);
    document.addEventListener('pointerout', out, true);
    document.addEventListener('pointerdown', hide, true);
    window.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('pointerover', over, true);
      document.removeEventListener('pointerout', out, true);
      document.removeEventListener('pointerdown', hide, true);
      window.removeEventListener('scroll', hide, true);
    };
  }, []);

  if (!tip) return null;
  return createPortal(
    <div
      ref={box}
      role="tooltip"
      className={`tip-box${tip.below ? ' below' : ''}`}
      style={{ left: tip.left, top: tip.top, visibility: tip.shown ? 'visible' : 'hidden', '--arrow': `${tip.arrow}px` } as React.CSSProperties}
    >
      <TipBody {...structure(tip.text)} />
    </div>,
    document.body,
  );
}
